import { SubscriptionPlan } from '../services/api';

/**
 * Utilities for managing plan selection across login flow
 */

const SELECTED_PLAN_KEY = 'treffpunkts_selected_plan';
const PLAN_SELECTION_TIMESTAMP_KEY = 'treffpunkts_plan_timestamp';
const FREE_TRIAL_SIGNUP_KEY = 'treffpunkts_free_trial_signup';

// Plan selection expires after 30 minutes
const PLAN_SELECTION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export interface StoredPlanSelection {
  plan: SubscriptionPlan;
  timestamp: number;
  source: 'pricing_page' | 'landing_page' | 'profile_page' | 'member_profile';
}

/**
 * Store selected plan in localStorage with timestamp
 */
export const storeSelectedPlan = (
  plan: SubscriptionPlan,
  source: StoredPlanSelection['source'] = 'pricing_page',
): void => {
  try {
    const planSelection: StoredPlanSelection = {
      plan,
      timestamp: Date.now(),
      source,
    };
    
    localStorage.setItem(SELECTED_PLAN_KEY, JSON.stringify(planSelection));
    console.log('📦 Stored selected plan:', plan.display_name, 'from', source);
  } catch (error) {
    console.error('❌ Error storing selected plan:', error);
  }
};

/**
 * Retrieve selected plan from localStorage if not expired
 */
export const getStoredPlan = (): StoredPlanSelection | null => {
  try {
    const storedData = localStorage.getItem(SELECTED_PLAN_KEY);
    if (!storedData) {
      return null;
    }

    const planSelection: StoredPlanSelection = JSON.parse(storedData);
    const now = Date.now();
    
    // Check if selection has expired
    if (now - planSelection.timestamp > PLAN_SELECTION_TIMEOUT) {
      console.log('⏰ Plan selection expired, clearing storage');
      clearStoredPlan();
      return null;
    }

    console.log('📦 Retrieved stored plan:', planSelection.plan.display_name);
    return planSelection;
  } catch (error) {
    console.error('❌ Error retrieving stored plan:', error);
    clearStoredPlan(); // Clear corrupted data
    return null;
  }
};

/**
 * Clear stored plan selection
 */
export const clearStoredPlan = (): void => {
  try {
    localStorage.removeItem(SELECTED_PLAN_KEY);
    localStorage.removeItem(PLAN_SELECTION_TIMESTAMP_KEY);
    console.log('🧹 Cleared stored plan selection');
  } catch (error) {
    console.error('❌ Error clearing stored plan:', error);
  }
};

/**
 * Check if there's a valid stored plan selection
 */
export const hasStoredPlan = (): boolean => {
  return getStoredPlan() !== null;
};

/**
 * Get plan selection age in minutes
 */
export const getPlanSelectionAge = (): number | null => {
  const storedPlan = getStoredPlan();
  if (!storedPlan) return null;
  
  return Math.floor((Date.now() - storedPlan.timestamp) / (60 * 1000));
};

/**
 * Generate user-friendly message for login prompt based on selected plan
 */
export const getPlanSelectionMessage = (plan: SubscriptionPlan): string => {
  const planName = plan.display_name;
  const price = `€${plan.price}`;
  const cycle = plan.billing_cycle === 'monthly' ? 'month' : 'year';
  
  return `Login to activate ${planName} (${price}/${cycle}) and start creating activities!`;
};

/**
 * Check if plan selection is from recent source (for analytics)
 */
export const isPlanSelectionRecent = (maxAgeMinutes: number = 5): boolean => {
  const age = getPlanSelectionAge();
  return age !== null && age <= maxAgeMinutes;
};

/**
 * Store free trial signup intent (when user clicks "Start Your Free Trial")
 */
export const storeFreeTrialSignupIntent = (): void => {
  try {
    localStorage.setItem(FREE_TRIAL_SIGNUP_KEY, 'true');
    console.log('🎯 Stored free trial signup intent');
  } catch (error) {
    console.error('❌ Error storing free trial signup intent:', error);
  }
};

/**
 * Check if user intended to sign up for free trial
 */
export const hasFreeTrialSignupIntent = (): boolean => {
  try {
    return localStorage.getItem(FREE_TRIAL_SIGNUP_KEY) === 'true';
  } catch (error) {
    console.error('❌ Error checking free trial signup intent:', error);
    return false;
  }
};

/**
 * Clear free trial signup intent
 */
export const clearFreeTrialSignupIntent = (): void => {
  try {
    localStorage.removeItem(FREE_TRIAL_SIGNUP_KEY);
    console.log('🧹 Cleared free trial signup intent');
  } catch (error) {
    console.error('❌ Error clearing free trial signup intent:', error);
  }
};